import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Bookmark, Trash2, BookOpen, FileText, Mic, Play, Sparkles, ShoppingCart, Headphones, Library as LibraryIcon, ExternalLink, Play as PlayIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LIB } from "@/constants/testIds/app";

const iconOf = { book: BookOpen, article: FileText, podcast: Mic, video: Play, exercise: Sparkles };
const linkIconOf = { read: ExternalLink, buy: ShoppingCart, audio: Headphones, library: LibraryIcon, listen: Headphones, watch: PlayIcon };
const STATUSES = ["reading", "finished", "helpful", "to-read", "to-buy"];
const TYPES = ["book", "article", "podcast", "video", "exercise"];

export default function Library() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/library");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/library/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/library/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const filtered = useMemo(() => {
    return items.filter((e) => {
      if (typeFilter !== "all" && e.item?.type !== typeFilter) return false;
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      return true;
    });
  }, [items, typeFilter, statusFilter]);

  const typeCounts = useMemo(() => {
    const c = { all: items.length };
    TYPES.forEach((t) => (c[t] = items.filter((e) => e.item?.type === t).length));
    return c;
  }, [items]);

  return (
    <div data-testid={LIB.container} className="max-w-5xl">
      <div className="label-caps mb-4">Your library</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2C2520] tracking-tight">
        A shelf of your own.
      </h1>
      <p className="mt-5 text-[#2C2520]/70 leading-relaxed max-w-xl">
        Every saved book, essay, and practice, kept softly in one place.
      </p>

      {loading ? (
        <p className="mt-10 text-[#2C2520]/60 italic font-serif-display">Opening the drawer…</p>
      ) : items.length === 0 ? (
        <div data-testid={LIB.empty} className="mt-14 border border-dashed border-[#E8E3D5] rounded-[12px] p-12 text-center bg-[#F5F2EA]/40">
          <Bookmark className="w-8 h-8 text-[#4A5D4E]/50 mx-auto" strokeWidth={1.5} />
          <p className="mt-4 font-serif-display italic text-lg text-[#2C2520]/70">
            Nothing saved yet.
          </p>
          <p className="text-sm text-[#2C2520]/55 mt-1">Do a mood check-in or write a journal entry to receive picks.</p>
        </div>
      ) : (
        <>
          <div className="mt-10 space-y-4">
            <div>
              <div className="label-caps mb-2">Kind</div>
              <div className="flex flex-wrap gap-2">
                <FilterChip
                  active={typeFilter === "all"}
                  onClick={() => setTypeFilter("all")}
                  testid="library-filter-type-all"
                >
                  All <span className="opacity-60 ml-1">·{typeCounts.all}</span>
                </FilterChip>
                {TYPES.map((t) => (
                  <FilterChip
                    key={t}
                    active={typeFilter === t}
                    onClick={() => setTypeFilter(t)}
                    testid={`library-filter-type-${t}`}
                  >
                    {t} <span className="opacity-60 ml-1">·{typeCounts[t]}</span>
                  </FilterChip>
                ))}
              </div>
            </div>
            <div>
              <div className="label-caps mb-2">Status</div>
              <div className="flex flex-wrap gap-2">
                <FilterChip
                  active={statusFilter === "all"}
                  onClick={() => setStatusFilter("all")}
                  testid="library-filter-status-all"
                >
                  All
                </FilterChip>
                {STATUSES.map((s) => (
                  <FilterChip
                    key={s}
                    active={statusFilter === s}
                    onClick={() => setStatusFilter(s)}
                    testid={`library-filter-status-${s}`}
                  >
                    {s}
                  </FilterChip>
                ))}
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="mt-10 text-[#2C2520]/60 italic font-serif-display" data-testid="library-empty-filtered">
              Nothing matches this filter.
            </p>
          ) : (
            <div className="mt-8 grid md:grid-cols-2 gap-5">
              {filtered.map((entry) => {
                const it = entry.item;
                const Icon = iconOf[it.type] || BookOpen;
                return (
                  <div
                    key={entry.id}
                    data-testid={LIB.item(entry.id)}
                    className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-5 flex gap-4"
                  >
                    <div className="w-16 h-20 rounded-[8px] bg-[#EDE8DB] overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {it.cover_url ? (
                        <img src={it.cover_url} alt="" className="w-full h-full object-cover" onError={(e) => (e.target.style.display = "none")} />
                      ) : (
                        <Icon className="w-6 h-6 text-[#4A5D4E]/60" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="label-caps">{it.type}</div>
                      <h3 className="font-serif-display text-lg text-[#2C2520] leading-snug mt-0.5">{it.title}</h3>
                      <p className="text-xs text-[#2C2520]/60 mt-0.5">{it.author}</p>

                      <div className="mt-3 flex items-center gap-2">
                        <select
                          data-testid={LIB.statusSelect(entry.id)}
                          value={entry.status}
                          onChange={(e) => updateStatus(entry.id, e.target.value)}
                          className="text-xs px-2 py-1.5 rounded-[8px] bg-[#FDFBF7] border border-[#E8E3D5] text-[#2C2520]"
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button
                          data-testid={LIB.deleteBtn(entry.id)}
                          onClick={() => remove(entry.id)}
                          className="p-1.5 rounded-[8px] text-[#2C2520]/50 hover:text-[#8B3A3A] hover:bg-[#F5E9E6]"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                      {it.access_links?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {it.access_links.slice(0, 3).map((lnk, li) => {
                            const LIcon = linkIconOf[lnk.kind] || ExternalLink;
                            return (
                              <a
                                key={li}
                                href={lnk.url}
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid={`library-access-${entry.id}-${lnk.kind}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] bg-[#FDFBF7] border border-[#E8E3D5] text-[#2C2520] hover:bg-[#EDE8DB] transition-colors"
                              >
                                <LIcon className="w-3 h-3" strokeWidth={1.5} />
                                {lnk.label}
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={
        "px-3 py-1.5 rounded-full text-xs capitalize transition-colors border " +
        (active
          ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
          : "bg-[#F5F2EA] text-[#2C2520] border-[#E8E3D5] hover:bg-[#EDE8DB]")
      }
    >
      {children}
    </button>
  );
}
